import { useEffect, useState } from 'react';
import axios from 'axios';
import properties from '../../../core-utils/properties';
import SortableTable, {
  Section,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components/organisms/SortableTable';
import {
  Add,
  BasicModal,
  Button,
  CardComponent,
  Table,
  TextField,
  Tooltip,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import React from 'react';
import AddParameter from '../AddParameter';
import useSnackbar from '../../../core-utils/custom-hooks/Snackbar';
import Snackbar from '../../atoms/Snackbar';
import { RECOMMENDED_APPS } from '../../../core-utils/messages/RecommendedAppConstants';
import { CircularLoader } from '@HomaHealth/ui-component-library';
import AddNewAndSubParameter from '../AddNewAndSubParameter';
import clsx from 'clsx';
import SvgInfo from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Icons/Info';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import NoTopics from '../../atoms/Icons/NoTopics';
import ReportCreateNewLabelField from '../../atoms/ReportCreateNewLabelField';
import {
  compilationObjectProps,
  updateCompiler,
} from '../../../core-utils/helper-utils/termCompilerHelper';
import { useStores } from '../../../stores';

export interface TermsSelectionProps {
  selectedTerms: any[];
  setSelectedTerms: React.Dispatch<React.SetStateAction<any[]>>;
  termSelectionComboPage: boolean;
  setTermSelectionComboPage: React.Dispatch<React.SetStateAction<boolean>>;
  compilationObject: compilationObjectProps;
  setCompilationObject: React.Dispatch<
    React.SetStateAction<compilationObjectProps>
  >;
}

const useStyles = makeStyles()(() => ({
  hr: {
    width: '100%',
    border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    margin: '18px 0px',
  },
  parametersGrid: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  noParametersGrid: {
    height: '312px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  tableRoot: {
    backgroundColor: 'white',
    '& tr > td:first-child': {
      paddingLeft: '16px',
    },
    '& thead th': {
      color: patientAccessTheme.palette.text.highemphasis,
    },
    '& table tbody tr:hover': {
      backgroundColor: 'transparent !important',
    },
    '& tbody tr': {
      height: '56px',
    },
    '& tbody td': {
      verticalAlign: 'middle',
    },
  },
  tableTHForRowTop: {
    color: `${patientAccessTheme.palette.text.highemphasis}`,
  },
  tableTHSelection: {
    width: '10px',
    paddingLeft: '12px',
    backgroundColor: `${patientAccessTheme.palette.grey[100]}`,
  },
  subRows: {
    backgroundColor: patientAccessTheme.palette.grey[100],
  },
  tableRow: {
    '& td:first-child': {
      paddingLeft: '15px',
    },
    alignItems: 'center',
  },
  emptyDataContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '16px 12px',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export const TermsSelection: React.FC<TermsSelectionProps> = ({
  selectedTerms,
  setSelectedTerms,
  termSelectionComboPage,
  setTermSelectionComboPage,
  compilationObject,
  setCompilationObject,
}) => {
  // Constants
  const message = RECOMMENDED_APPS.TERM_COMPILER;
  const { classes } = useStyles();
  const { openSnackbar, snackbarState, handleCloseSnackBar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(false);
  const [customParams, setCustomParams] = useState<
    { id: string; name: string; description: string }[]
  >([]);
  const commonTableProps = {
    showPagination: false,
    showSelection: true,
    showFiltering: false,
    overallGridStyles: classes.tableRoot,
    classNameForTableHeaderRowTop: classes.tableTHForRowTop,
    classNameForTableHeaderRowForSelection: clsx(
      classes.tableTHForRowTop,
      classes.tableTHSelection,
    ),
    isManualPagination: true,
    controlledPageIndex: 0,
    showEditData: true,
    hideSelectionForSubRows: true,
    hideSelectionHeader: false,
    showTableHeaderTopRightComponent: false,
    showSelectionCount: true,
  };
  const columns = [
    {
      Header: 'Name',
      accessor: 'name',
      Cell: ({ row }: { row: any }) => (
        <Grid
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '16px 0px',
            height: '100%',
          }}
        >
          <Typography
            fontType="semiBold"
            textColor={`${patientAccessTheme.palette.text.highemphasis}`}
            fontSize="14px"
            styleProps={{
              margin: 0,
              lineHeight: '1',
              paddingLeft: '16px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {row.original.name}
          </Typography>
          {/* <Grid style={{ display: 'flex', alignItems: 'center' }}>
                <SvgInfo />
              </Grid> */}
          {row?.original?.description && (
            <Tooltip
              title={
                <Typography
                  fontSize="13px"
                  lineHeight="19.5px"
                  fontType="semiBold"
                  textColor={patientAccessTheme.palette.status.white}
                >
                  {row?.original?.description}
                </Typography>
              }
              placement="top-end"
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <SvgInfo />
              </div>
            </Tooltip>
          )}
        </Grid>
      ),
    },
  ];
  const LoginPageStore = useStores().loginPageStore;

  // State
  const [isOpenAddParameter, setIsOpenAddParameter] = useState<boolean>(false);
  const [isOpenSubParameter, setIsOpenSubParameter] = useState<boolean>(false);
  // const [parameterEditMode, setParameterEditMode] = useState<boolean>(false);
  const [subParameterEditMode, setSubParameterEditMode] = useState<boolean>(
    false,
  );
  const [searchText, setSearchText] = useState<string>('');
  const [newUseCaseName, setNewUseCaseName] = useState<string>('');
  const [isUseCaseActive, setIsUseCaseActive] = useState<boolean>(false);

   
  const [topicsAndTerms, setTopicsAndTerms] = useState<any[]>([]);
  const [isAddCustom, setIsAddCustom] = useState(false);

  const [selectedTerm, setSelectedTerm] = useState<{
    id: string;
    name: string;
    value?: string;
    sectionTopic?: string;
    sectionLabel?: string;
    rows?: any[];
  }>({
    id: '',
    name: '',
    value: '',
    sectionTopic: '',
    sectionLabel: '',
    rows: [],
  });

  const [selectedSubParameter, setSelectedSubParameter] = useState<{
    id: string;
    name: string;
    value: string;
  }>({ id: '', name: '', value: '' });
  const [newUseCasePayload, setNewUseCasePayload] = useState<any>(null);
  const [selectedCustomParams, setSelectedCustomParams] = useState<
    { id: string; name: string; description: string }[]
  >([]);
  const [isOpenEditTopic, setIsOpenEditTopic] = useState<boolean>(false);

  // ---------------------------
  // PARAMETER HANDLERS
  // ---------------------------
  const handleAddParameter = (
    id: string,
    sectionTopic?: string,
    sectionLabel?: string,
    rows?: any[],
  ) => {
    // setParameterEditMode(false);
    setSelectedTerm({
      id,
      name: '',
      value: '',
      sectionTopic,
      sectionLabel,
      rows,
    });
    setIsOpenAddParameter(true);
  };

  const handleEditParameter = (row: any) => {
    setSelectedTerm({
      id: row.id,
      name: row.name,
      value: row.value,
    });
    setIsOpenEditTopic(true);
  };

  const handleDeleteParameter = (row: any) => {
    setSelectedTerms((prev) =>
      prev.map((section) => ({
        ...section,
        selectedTopics: section.selectedTopics.filter(
           
          (topic: string) => topic !== row.name,
        ),
        rows: section.rows.filter(
           
          (r: { id: string }) => r.id !== row.id,
        ),
      })),
    );
  };

  // ---------------------------
  // SUB PARAMETER HANDLERS
  // ---------------------------
  const handleAddSubParameter = (parentId: number) => {
    setSelectedTerm({ id: String(parentId), name: '' });
    setSelectedSubParameter({ id: '', name: '', value: '' });
    setSubParameterEditMode(false);
    setIsOpenSubParameter(true);
  };

  const handleChangeSubParameter = (
    field: 'name' | 'description' | 'type',
    value: string,
  ) =>
    setSelectedSubParameter((prev) => ({
      ...prev,
      [field === 'name' ? 'name' : 'value']: value,
    }));

  const handleSaveSubParameter = () => {
    const updatedTerms = selectedTerms.map((section) => ({
      ...section,
       
      rows: section.rows.map((row: { id: string; subrows: any[] }) =>
        row.id === selectedTerm.id
          ? {
              ...row,
              subrows: subParameterEditMode
                ?  
                  row.subrows?.map((sub: { id: string }) =>
                    sub.id === selectedSubParameter.id
                      ? {
                          ...sub,
                          name: selectedSubParameter.name,
                          value: selectedSubParameter.value,
                        }
                      : sub,
                  ) || []
                : [
                    ...(row.subrows || []),
                    {
                      id: Date.now().toString(),
                      name: selectedSubParameter.name,
                      value: selectedSubParameter.value,
                      key: selectedSubParameter.name
                        .toLowerCase()
                        .replace(/\s+/g, '_')
                        .replace(/[^a-z0-9_]/g, ''),
                      type: 'TEXT',
                    },
                  ],
            }
          : row,
      ),
    }));
    setSelectedTerms(updatedTerms);
    setIsOpenSubParameter(false);
    handleUseCaseCheck(updatedTerms);
  };

  const handleUseCaseCheck = (updatedData: any) => {
    // Then build the payload as before
    const topics = updatedData.map((section: any) => {
      const terms: any[] = [];
      section.rows?.forEach((row: any) => {
         
        row?.subrows?.forEach((sub: any) => {
          terms.push({
            topic: row.name,
            topic_description: sub?.description ?? row.value,
            term: sub.name,
            description: sub.value,
            key:
              sub.key ||
              sub.name
                .toLowerCase()
                .replace(/\s+/g, '_')
                .replace(/[^a-z0-9_]/g, ''),
            type: sub.type || 'TEXT',
          });
        });
      });

      return {
        topic: section.topic,
        label: section.title,
        terms,
      };
    });

    const payload = {
      use_case: compilationObject.is_default_use_case
        ? ''
        : compilationObject.use_case_name,
      topics,
      isDefault: false,
      eg_nid: compilationObject.group,
      organization: LoginPageStore.userResponse.organizationName,
    };

    if (compilationObject.is_default_use_case) {
      setNewUseCasePayload(payload);
      setIsUseCaseActive(true);
    } else {
      handleAddNewUseCase(payload);
    }
  };

  const handleCloseSubParameter = () => {
    setSelectedSubParameter({ id: '', name: '', value: '' });
    setIsOpenSubParameter(false);
  };

  const handleEditSubParameter = (subRow: any, parentRowId: number) => {
    setSubParameterEditMode(true);
    setSelectedTerm({ id: String(parentRowId), name: '' });
    setSelectedSubParameter(subRow);
    setIsOpenSubParameter(true);
  };

  const handleDeleteSubParameter = (subRowId: number, parentRowId: number) => {
    setSelectedTerms((prev) =>
      prev.map((section) => ({
        ...section,
         
        rows: section.rows.map((row: { id: string; subrows: any[] }) =>
          row.id === String(parentRowId)
            ? {
                ...row,
                subrows: row.subrows?.filter(
                   
                  (sub: { id: string }) => sub.id !== String(subRowId),
                ),
              }
            : row,
        ),
      })),
    );
  };

  const handleSaveEditedTopic = () => {
    const updatedTerms = selectedTerms.map((section) => ({
      ...section,
      rows: section.rows.map((row: any) =>
        row.id === selectedTerm.id
          ? {
              ...row,
              name: selectedTerm.name,
              value: selectedTerm.value,
            }
          : row,
      ),
    }));

    setIsOpenEditTopic(false);

    if (compilationObject.is_default_use_case) {
      // Defer update until use case name is provided
      setNewUseCasePayload({
        use_case: '', // name will come from modal input
        topics: updatedTerms.map((section: any) => {
          const terms: any[] = [];
          section.rows?.forEach((row: any) => {
             
            row?.subrows?.forEach((sub: any) => {
              terms.push({
                topic: row.name,
                topic_description: sub?.description ?? row.value,
                term: sub.name,
                description: sub.value,
                key:
                  sub.key ||
                  sub.name
                    .toLowerCase()
                    .replace(/\s+/g, '_')
                    .replace(/[^a-z0-9_]/g, ''),
                type: sub.type || 'TEXT',
              });
            });
          });

          return {
            topic: section.topic,
            label: section.title,
            terms,
          };
        }),
        isDefault: false,
        eg_nid: compilationObject.group,
        organization: LoginPageStore.userResponse.organizationName,
        __pendingUpdatedTerms: updatedTerms,
      });

      setIsUseCaseActive(true);
    } else {
      // Save immediately
      setSelectedTerms(updatedTerms);
      handleUseCaseCheck(updatedTerms);
    }
  };

  // premature updates
  const applyUpdatedTerms = (updatedTerms: any) => {
    if (compilationObject.is_default_use_case) {
      setNewUseCasePayload({
        use_case: '',
        topics: updatedTerms.map((section: any) => {
          const terms: any[] = [];
          section.rows?.forEach((row: any) => {
             
            row?.subrows?.forEach((sub: any) => {
              terms.push({
                topic: row.name,
                topic_description: sub?.description ?? row.value,
                term: sub.name,
                description: sub.value,
                key:
                  sub.key ||
                  sub.name
                    .toLowerCase()
                    .replace(/\s+/g, '_')
                    .replace(/[^a-z0-9_]/g, ''),
                type: sub.type || 'TEXT',
              });
            });
          });

          return {
            topic: section.topic,
            label: section.title,
            terms,
          };
        }),
        isDefault: false,
        organization: LoginPageStore.userResponse.organizationName,
        eg_nid: compilationObject.group,
        __pendingUpdatedTerms: updatedTerms,
      });

      setIsUseCaseActive(true); // Open the modal
    } else {
      setSelectedTerms(updatedTerms); // Apply directly
      handleUseCaseCheck(updatedTerms);
    }
  };

  // ---------------------------
  // FORMAT TO ROW DATA
  // ---------------------------

  const finalSave = (topicData?: { name: string; description: string }) => {
    if (!topicData || !topicData.name) {
      return;
    }
    const newSubrows = selectedCustomParams.map((term) => ({
      id: Date.now().toString() + Math.random(),
      name: term.name,
      value: term.description,
      description: topicData.description,
      key: term.name
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, ''),
      type: 'TEXT',
    }));
    const updated = selectedTerms.map((section) => {
      if (section.id !== selectedTerm.id) {
        return section;
      }

      const updatedRows = [...(section.rows || [])];
      const existingRowIndex = updatedRows.findIndex(
        (row: any) => row.name === topicData.name,
      );

      if (existingRowIndex !== -1) {
        // Append subrows to existing topic row
        updatedRows[existingRowIndex] = {
          ...updatedRows[existingRowIndex],
          subrows: [
            ...(updatedRows[existingRowIndex].subrows || []),
            ...newSubrows,
          ],
        };
      } else {
        // Create a new row with these custom terms
        updatedRows.push({
          id: Date.now().toString(),
          name: topicData.name,
          topic_description: topicData,
          subrows: newSubrows,
        });
      }

      const updatedTopics = new Set([
        ...(section.selectedTopics || []),
        topicData.name,
        topicData.description,
      ]);

      return {
        ...section,
        rows: updatedRows,
        selectedTopics: Array.from(updatedTopics),
      };
    });
    setCustomParams([]);
    setIsOpenAddParameter(false);
    applyUpdatedTerms(updated);
  };

  const formatToRowData = (
    sections: any[],
    selectedTopicsBySection: Record<number, string[]>,
  ): Section[] => {
    const buildRows = (
      section: any,
      sectionIndex: number,
      selectedTopics: string[],
      existingRows: any[], // pass current rows to preserve edits
    ) => {
      if (!section.terms) {
        return [];
      }

      return selectedTopics
        .map((topicName) => {
          // check if row already exists (edited/preserved)
          const existingRow = existingRows.find((r) => r.name === topicName);
          if (existingRow) {
            return existingRow;
          }

          // find topic in API data
          const topicData = section.terms.find(
            (t: any) => t.topic === topicName,
          );
          if (!topicData) {
            return null;
          }

          return {
            id: topicData.id || `${sectionIndex}-${topicName}`,
            name: topicData.topic,
            value: topicData.topic_description,
            subrows: topicData.terms?.map((term: any, termIndex: number) => ({
              id: term.id || `${sectionIndex}-${topicName}-${termIndex}`,
              name: term.term,
              value: term.description,
              key: term.key,
              type: term.type,
            })),
          };
        })
        .filter(Boolean);
    };

    return sections.map((section, sectionIndex) => {
      const selectedTopics =
        selectedTopicsBySection[sectionIndex] ||
        section?.terms?.map((t: any) => t.topic) ||
        [];
      return {
        id: `section-${sectionIndex + 1}`,
        title: section.label,
        topic: section.topic,
        topics: section?.terms?.map((t: any) => t.topic) || [],
        selectedTopics,
        handleChange: (topics: string[]) => {
          setSelectedTerms((prev) =>
             
            prev.map((s, i) =>
              i === sectionIndex
                ? {
                    ...s,
                    selectedTopics: topics,
                    rows: buildRows(
                      sections[sectionIndex],
                      sectionIndex,
                      topics,
                      s.rows || [], // preserve existing rows
                    ),
                  }
                : s,
            ),
          );
        },
        handleAddTopic: () => {
          handleAddParameter(
            `section-${sectionIndex + 1}`,
            section.topic,
            section.label,
            buildRows(section, sectionIndex, selectedTopics, []),
          );
        },
        rows: buildRows(section, sectionIndex, selectedTopics, []),
      };
    });
  };

  const getFilteredRows = () => {
    if (termSelectionComboPage) {
      // ✅ combo = topic contains "+"
      return selectedTerms.filter((s: any) => s.topic?.includes('+'));
    } else {
      // ✅ non-combo = topic does NOT contain "+"
      return selectedTerms.filter((s: any) => !s.topic?.includes('+'));
    }
  };

  // ---------------------------
  // API CALL
  // ---------------------------
  const getTopicsAndTerms = async (use_case_id?: string) => {
    try {
      setLoading(true);
      const res = await axios.post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.getTopicsAndTerms,
        {
          use_case_id: use_case_id ?? compilationObject.use_case,
          contracts: compilationObject.contract_ids,
          plan_docs: compilationObject.plan_docs,
        },
      );
      if (res.status === 200) {
        setTopicsAndTerms(res.data.data);
        setSelectedTerms(formatToRowData(res.data.data, {}));
      }
    } catch (error: any) {
      openSnackbar('Something went wrong', 'error', 6000);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewUseCase = async (basePayload?: any) => {
    try {
      const finalUseCaseName = compilationObject.is_default_use_case
        ? newUseCaseName
        : basePayload?.use_case;

      if (!finalUseCaseName) {
        openSnackbar('Please enter a valid use case name', 'error', 4000);
        return;
      }

      const payload = {
        ...basePayload,
        use_case: finalUseCaseName,
      };
      setLoading(true);
      setIsUseCaseActive(false);
      const res = await axios.post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.addUseCaseAndTopics,
        payload,
      );

      if (res.status === 200) {
        setCompilationObject((prev) => ({
          ...prev,
          use_case: res?.data.data._id,
          use_case_name: res?.data?.data?.use_case,
          is_default_use_case: false,
        }));
        openSnackbar('Use case saved successfully!', 'success', 3000);
        setIsUseCaseActive(false);
        updateCompiler(
          {
            use_case: res.data.data._id,
          },
          compilationObject.term_compiler_id,
          openSnackbar,
        );
        getTopicsAndTerms(res?.data.data._id);
      }
    } catch (error) {
      openSnackbar('Failed to save use case', 'error', 5000);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = React.useMemo(() => {
    const trimmedSearch = searchText.trim().toLowerCase();
    const fullData = customParams;

    if (trimmedSearch.length < 3) {
      return fullData;
    }

    return fullData.filter((item) => {
      const name = item.name?.toLowerCase() || '';
      return name.includes(trimmedSearch);
    });
  }, [searchText, customParams]);

  useEffect(() => {
    getTopicsAndTerms();
  }, []);

  return (
    <>
      <CardComponent
        title={message.SELECTION}
        subTitle={message.SELECTION_SUBTITLE}
        mainGridStyles={{ marginTop: '10px' }}
        body={
          <>
            <hr className={classes.hr} />
            {loading ? (
              <CircularLoader />
            ) : (
              <>
                <SortableTable
                  multipleTable={true}
                  columns={[
                    { name: 'Topic Name', width: '30%' },
                    { name: 'Description', width: '60%' },
                    { name: '', width: '' },
                  ]}
                  rows={getFilteredRows()}
                  setRows={setSelectedTerms}
                  handleAddSubParameter={handleAddSubParameter}
                  handleEdit={handleEditSubParameter}
                  handleDelete={handleDeleteSubParameter}
                  tableWidth="100%"
                  handleDeleteParameter={handleDeleteParameter}
                  handleEditParameter={handleEditParameter}
                  addSubParameterLabel={message.ADD_TERMS}
                />
                <div style={{ marginTop: '16px', textAlign: 'right' }}>
                  {!selectedTerms.some((s: any) => s.topic?.includes('+')) ? (
                    <></>
                  ) : (
                    <>
                      {!selectedTerms.some((s: any) =>
                        s.topic?.includes('+'),
                      ) ? null : (
                        <>
                          {!termSelectionComboPage ? (
                            <Button
                              onClick={() => setTermSelectionComboPage(true)}
                              label="Next"
                            />
                          ) : (
                            <Button
                              onClick={() => setTermSelectionComboPage(false)}
                              label="Go Back"
                            />
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </>
        }
      />

      {/* Add/Edit Parameter */}
      {/* {isOpenAddParameter && (
        <AddParameter
          name={selectedTerm.name}
          description={selectedTerm.value}
          onChange={handleChangeParameter}
          onSave={handleParameterSave}
          onClose={handleCloseAddParameter}
          title={parameterEditMode ? 'Edit Parameter' : 'Add Parameter'}
        />
      )} */}

      {isOpenAddParameter && (
        <AddNewAndSubParameter
          title="Add Topic"
          topicName={selectedTerm?.name || ''}
          topicDescription={selectedTerm?.value || ''}
          cancelButtonLabel="Cancel"
          saveButtonLabel="Save"
          subTitle="Terms"
          searchText={searchText}
          onSearchChange={(e) => setSearchText(e.target.value)}
          customParams={customParams}
          setCustomParams={setCustomParams}
          isConfirmDisabled={selectedCustomParams.length === 0}
          onTopicSave={(topicData) => {
            // setNewTopicData(topicData);
            finalSave(topicData);
          }}
          selectedTerms={selectedCustomParams}
          isAddCustom={isAddCustom}
          setIsAddCustom={setIsAddCustom}
          subBody={
            <Table
              {...commonTableProps}
              columns={columns}
              data={customParams}
              onHoverRowData={() => {}}
              showPagination={false}
              selectedRowIds={selectedCustomParams.map((item) => item.name)}
              isManualPagination={true}
              showSelectionCount={filteredData?.length !== 0}
              isDataEmpty={filteredData?.length === 0}
              onSelectedRowsChange={(
                selectedRowIds: Record<number, boolean>,
              ) => {
                const selectedTerms = Object.keys(selectedRowIds)
                  .map((index) => customParams[parseInt(index)])
                  .filter(Boolean);
                setSelectedCustomParams(selectedTerms);
              }}
              emptyDataWidget={
                <Grid className={classes.emptyDataContainer}>
                  <NoTopics />
                  <Button
                    label="Add Terms"
                    width="auto"
                    color="primary"
                    variant="outlined"
                    styleProps={{
                      fontSize: '12px',
                    }}
                    startIcon={<Add fill="#475CB0" />}
                    onClick={() => {
                      setIsAddCustom(true);
                    }}
                  />
                </Grid>
              }
            />
          }
          handleClose={() => {
            setSelectedTerm({ id: '', name: '', value: '' });
            setCustomParams([]);
            setIsOpenAddParameter(false);
          }}
        />
      )}

      {/* Add/Edit SubParameter */}
      {isOpenSubParameter && (
        <AddParameter
          name={selectedSubParameter.name}
          description={selectedSubParameter.value}
          onChange={handleChangeSubParameter}
          onSave={handleSaveSubParameter}
          onClose={handleCloseSubParameter}
          title={subParameterEditMode ? 'Edit Term' : 'Add Term'}
        />
      )}

      <BasicModal
        title={'Edit Topic Details'}
        open={isOpenEditTopic}
        body={
          <>
            <ReportCreateNewLabelField
              label="Name"
              width="100%"
              component={
                <TextField
                  value={selectedTerm.name}
                  fullWidth
                  height="36px"
                  onChange={(e: any) =>
                    setSelectedTerm((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  placeholder="Enter Name"
                />
              }
            />
            <ReportCreateNewLabelField
              label="Description"
              width="100%"
              component={
                <TextField
                  value={selectedTerm.value}
                  fullWidth
                  height="36px"
                  onChange={(e: any) =>
                    setSelectedTerm((prev) => ({
                      ...prev,
                      value: e.target.value,
                    }))
                  }
                  placeholder="Enter Description"
                />
              }
            />
          </>
        }
        cancelButtonLabel={'Cancel'}
        confirmButtonLabel={'Save'}
        handleCancel={() => {
          setIsOpenEditTopic(false);
        }}
        handleConfirm={handleSaveEditedTopic}
        handleClose={function (): void {
          setIsOpenEditTopic(false);
        }}
      />

      <BasicModal
        title={'Add New Use Case'}
        open={isUseCaseActive}
        body={
          <ReportCreateNewLabelField
            label="Use case name"
            width="100%"
            component={
              <TextField
                value={newUseCaseName}
                fullWidth
                height="36px"
                onChange={(e: any) => setNewUseCaseName(e.target.value)}
                placeholder="Enter Name"
              />
            }
          />
        }
        cancelButtonLabel={'Cancel'}
        confirmButtonLabel={'Add'}
        handleCancel={() => {
          setNewUseCaseName('');
          setIsUseCaseActive(false);
          setCustomParams([]);
        }}
        handleConfirm={async () => {
          if (!newUseCaseName?.trim()) {
            openSnackbar('Please enter a valid use case name', 'error', 4000);
            return;
          }
          if (newUseCasePayload) {
            // Set the correct name just in case
            const payload = {
              ...newUseCasePayload,
              use_case: newUseCaseName.trim(),
            };

            await handleAddNewUseCase(payload); // Save to backend

            // ✅ Apply saved edits to UI
            if (newUseCasePayload.__pendingUpdatedTerms) {
              setSelectedTerms(newUseCasePayload.__pendingUpdatedTerms);
              handleUseCaseCheck(newUseCasePayload.__pendingUpdatedTerms);
            }

            // Cleanup
            setNewUseCaseName('');
            setNewUseCasePayload(null);
            setIsUseCaseActive(false);
          }
        }}
        handleClose={function (): void {
          setIsUseCaseActive(false);
        }}
      />
      <Snackbar
        {...snackbarState}
        handleClose={handleCloseSnackBar}
        vertical="bottom"
        horizontal="right"
      />
    </>
  );
};
